import { AppType } from "client/Api/Apps/common";
import {
	Button,
	FormItem,
	InputRow,
	Select,
	SelectItem,
	ValidationError
} from "client/Components";
import {
	AppDestinationWindowSelectFormItem
} from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts
} from "client/Hooks";
import { createValidationConfiguration } from "client/Validation";
import {
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import { EditAppSavedResult, useSaveApp } from "../useSaveApp";
import {
	MagisterConnectionSelectOptionsModel,
	getMagisterConnections,
	MagisterConnectionSelectOptionModel
} from "client/Api/Apps/EditApp/appParameters";
import { MagisterConnectionDialog } from "client/Features/Settings/Connections/Magister/MagisterConnectionDialog";
import { MagisterConnectionListItemModel } from "client/Api/MagisterConfiguration/magisterConfiguration";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	EditMagisterAppViewModel,
	SaveMagisterAppModel,
	saveMagisterApp
} from "client/Api/Apps/EditApp/saveApp";

export type EditMagisterAppProps = {
	initialSettings: EditAppSettings<EditMagisterAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditMagisterApp = forwardRef(
	(
		props: EditMagisterAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [isConnectionDialogOpen, setIsConnectionDialogOpen] = useState<boolean>(false);
		const [connections, setConnections] = useState<MagisterConnectionSelectOptionsModel>({
			canCreateNew: false,
			options: []
		});

		const [formData, setFormData] = useState<SaveMagisterAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const connectionOptions: readonly SelectItem<number>[] = useMemo(
			(): readonly SelectItem<number>[] => {
				return connections.options
					.map(
						(x): SelectItem<number> => ({
							value: x.id,
							text: x.name
						})
					);
			},
			[connections.options]
		);

		const alertManager: AlertManager = useAlerts();

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const formHandler: IFormHandler<SaveMagisterAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const loadMagisterConnections = useCallback(
			async (signal: AbortSignal): Promise<void> => {
				try {
					const result: MagisterConnectionSelectOptionsModel =
						await getMagisterConnections(signal);

					setConnections(result);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			},
			[alertManager]
		);

		useEffect(
			() => {
				const cancellation = new AbortController();
				loadMagisterConnections(cancellation.signal);
				return () => cancellation.abort();
			},
			[loadMagisterConnections]
		);

		const addConnectionHandler = useCallback(
			(item: MagisterConnectionListItemModel): void => {
				setConnections(
					(existingOptionsModel: MagisterConnectionSelectOptionsModel) => {
						const existingItemIndex: number = existingOptionsModel.options
							.findIndex(i => i.id === item.id);

						const options: MagisterConnectionSelectOptionModel[] = [
							...existingOptionsModel.options
						];

						if (existingItemIndex === -1) {
							options.push(item);
						} else {
							options.splice(
								existingItemIndex,
								1,
								item
							);
						}

						return {
							...existingOptionsModel,
							options
						};
					}
				);

				const attachable: IValueInputAttachable<number> = formHandler.for("connectionId");

				attachable
					.attachToSelect()
					.onChange(item.id);

				attachable
					.validation
					.triggerInputFinished();
			},
			[formHandler]
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveMagisterAppModel>(
			formHandler,
			saveMagisterApp,
			AppType.Magister,
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under("connectionId", "destinationWindow")
			.attachToTab()
			.hasValidationError;

		return (
			<>
				<EditAppBase
					formHandler={formHandler}
					hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
					initialAppSettings={app}
					usersAndUserGroupsData={usersAndUserGroupsData}
					initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
				>
					<FormItem label={localize("_Connection")}>
						<InputRow>
							<InputRow.Stretch>
								<Select
									{...formHandler.for("connectionId")
										.attachToSelect()}
									options={connectionOptions}
									placeholder={localize("_SelectConnection")}
									menuTitle={localize("_Connection")}
								/>
							</InputRow.Stretch>
							{connections.canCreateNew && (
								<Button
									text={localize("_Add")}
									onClick={() => setIsConnectionDialogOpen(true)}
									kind="secondary"
								/>
							)}
						</InputRow>
						<ValidationError
							{...formHandler.for("connectionId").attachToValidationError()}
						/>
					</FormItem>
					<AppDestinationWindowSelectFormItem
						attachable={formHandler.for("destinationWindow")}
					/>
				</EditAppBase>
				{isConnectionDialogOpen && (
					<MagisterConnectionDialog
						onAfterSave={addConnectionHandler}
						onClose={() => setIsConnectionDialogOpen(false)}
					/>
				)}
			</>
		);
	}
);

EditMagisterApp.displayName = "EditMagisterApp";

const validation = createValidationConfiguration<SaveMagisterAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forScalar("connectionId")
	.add(
		isNotNullNorUndefined()
	);

function convertToSaveModel(
	initialSettings: EditAppSettings<EditMagisterAppViewModel>
): SaveMagisterAppModel {
	const {
		connectionId,
		destinationWindow
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		connectionId,
		destinationWindow,
	};
}
